import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { useNotifications } from '../contexts/NotificationContext';
import { getToken } from '../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * useFollowingBadge — lightweight poll for unseen offers/events from
 * followed businesses. Returns { hasUpdates, markSeen } and works for both
 * logged-in users (Bearer token) and anonymous devices (subscriptionId).
 *
 * Intentionally minimal: no count, single boolean. Polls every 60s plus on
 * window focus. The Following page calls `markSeen()` on mount which
 * clears the dot.
 */
const useFollowingBadge = () => {
  const { user } = useAuth();
  const notifications = useNotifications();
  const subscriptionId = notifications?.subscriptionId || '';

  const [hasUpdates, setHasUpdates] = useState(false);

  const buildConfig = useCallback(() => {
    const headers = {};
    const token = getToken();
    if (user && token) headers.Authorization = `Bearer ${token}`;
    const params = subscriptionId ? { subscriptionId } : {};
    return { headers, params };
  }, [user, subscriptionId]);

  const check = useCallback(async () => {
    // Skip the call when we have no identity at all — nothing to follow.
    if (!user && !subscriptionId) {
      setHasUpdates(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/following/has-updates`, buildConfig());
      setHasUpdates(Boolean(res.data?.hasUpdates));
    } catch {
      // Silent — badge is a nice-to-have, never block the UI.
    }
  }, [user, subscriptionId, buildConfig]);

  const markSeen = useCallback(async () => {
    setHasUpdates(false);
    if (!user && !subscriptionId) return;
    try {
      await axios.post(`${API}/api/following/mark-seen`, null, buildConfig());
    } catch {
      // Silent — local state already cleared.
    }
  }, [user, subscriptionId, buildConfig]);

  useEffect(() => {
    check();
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    const id = setInterval(check, 60_000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(id);
    };
  }, [check]);

  return { hasUpdates, markSeen };
};

export default useFollowingBadge;
